import React, { useState, useEffect, useRef } from 'react';
import { userKey } from '../utils/storageHelper.js';
import { supabase } from '../lib/supabaseClient.js';
import { getDocCount } from '../utils/docCounter.js';
import classifierData from '../data/eu-ai-act-classifier.json';
import riskData from '../data/risk-calculator-questions.json';
import { AIDecisionsTab } from './AIDecisionsTab';
import complianceChecklists from '../data/ComplianceChecklists.json';
import { getDeadlineStatus } from '../data/complianceDeadlines.js';
import { EUFlagIcon } from './EUFlagIcon.jsx';


// ── Animated Number Counter ──
function AnimatedNumber({ value, suffix = '', duration = 1200 }) {
  const [display, setDisplay] = useState(0);
  const startTime = useRef(null);
  const startValue = useRef(0);

  const durationRef = useRef(duration);

  useEffect(() => {
    startValue.current = 0;
    startTime.current = null;
    const d = durationRef.current;
    const animate = (timestamp) => {
      if (!startTime.current) startTime.current = timestamp;
      const elapsed = timestamp - startTime.current;
      const progress = Math.min(elapsed / d, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(startValue.current + (value - startValue.current) * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  return <span>{display}{suffix}</span>;
}

// ── Radial Progress Ring ──
function RadialRing({ percent, color, size = 120, stroke = 10, label, sublabel }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={color} strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)' }}
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <span style={{ fontSize: '22px', fontWeight: 'bold', color, lineHeight: 1 }}>{percent}%</span>
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', margin: 0 }}>{label}</p>
        {sublabel && <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0' }}>{sublabel}</p>}
      </div>
    </div>
  );
}

// ── Mini Bar Chart ──
function MiniBarChart({ data, color }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '48px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
          <div style={{
            width: '100%', backgroundColor: color,
            height: `${Math.max((d.value / max) * 40, 2)}px`,
            borderRadius: '3px 3px 0 0',
            opacity: i === data.length - 1 ? 1 : 0.4 + (i / data.length) * 0.5,
            transition: 'height 0.8s ease',
          }} />
        </div>
      ))}
    </div>
  );
}

// ── Sparkline ──
function Sparkline({ values, color, width = 80, height = 32 }) {
  if (!values || values.length < 2) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points.split(' ').pop().split(',')[0]} cy={points.split(' ').pop().split(',')[1]} r="3" fill={color} />
    </svg>
  );
}

// ════════════════════════════════════════
// ONBOARDING FORM
// ════════════════════════════════════════
function OnboardingForm({ onComplete, lockedData }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    // Company fields
    companyName: '', industry: '', companySize: '',
    // Legacy text field (kept for backward compatibility)
    aiSystems: '',
// Structured AI system profile (Phase 1 enrichment)
    aiSystemProfile: {
      name: '',
      purpose: '',
      modelType: '',
      // EU AI Act Art. 25 — provider vs deployer distinction
      usesFoundationModel: '',
      foundationModel: '',
      // ISO 42001 § 6.1.2 — impact severity
      impactSeverity: '',
      dataCategories: [],
      affectedUsers: [],
      lifecycleStage: '',
      businessOwner: '',
      technicalOwner: '',
      // EU AI Act Art. 14 — human oversight
      humanOversight: [],
    },
    // Compliance profile
    primaryGoal: '', currentGovernance: '',
    euAiRisk: '', hasDataPrivacy: '', deploymentRegions: ''
  });

  const updateSystem = (key, val) => setForm(p => ({
    ...p,
    aiSystemProfile: { ...p.aiSystemProfile, [key]: val }
  }));

  const toggleDataCategory = (cat) => setForm(p => {
    const current = p.aiSystemProfile.dataCategories || [];
    const updated = current.includes(cat)
      ? current.filter(c => c !== cat)
      : [...current, cat];
    return { ...p, aiSystemProfile: { ...p.aiSystemProfile, dataCategories: updated } };
  });


const toggleAffectedUser = (group) => setForm(p => {
    const current = p.aiSystemProfile.affectedUsers || [];
    const updated = current.includes(group)
      ? current.filter(c => c !== group)
      : [...current, group];
    return { ...p, aiSystemProfile: { ...p.aiSystemProfile, affectedUsers: updated } };
  });


  const [saving, setSaving] = useState(false);
  const isEditMode = !!lockedData;

  // Pre-fill form with existing onboarding data when editing

React.useEffect(() => {
    if (lockedData) {
      const migrated = { ...lockedData };
      // Migrate old data: affectedUsers used to be a string, now an array
      if (migrated.aiSystemProfile && typeof migrated.aiSystemProfile.affectedUsers === 'string') {
        migrated.aiSystemProfile.affectedUsers = [];
      }
      // Migrate old data: humanOversight should be an array
      if (migrated.aiSystemProfile && !Array.isArray(migrated.aiSystemProfile.humanOversight)) {
        migrated.aiSystemProfile.humanOversight = [];
      }
      // Migrate old data: usesFoundationModel should be string ('', true, or false)
      if (migrated.aiSystemProfile && migrated.aiSystemProfile.usesFoundationModel === undefined) {
        migrated.aiSystemProfile.usesFoundationModel = '';
      }
      // Migrate old data: ensure new fields exist
      if (migrated.aiSystemProfile && !migrated.aiSystemProfile.foundationModel) {
        migrated.aiSystemProfile.foundationModel = '';
      }
      if (migrated.aiSystemProfile && !migrated.aiSystemProfile.impactSeverity) {
        migrated.aiSystemProfile.impactSeverity = '';
      }
      setForm(prev => ({ ...prev, ...migrated }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleComplete = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('user_profiles').upsert({
          user_id: user.id,
          onboarding_data: form,
          onboarding_completed: true,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });
      }
    } catch (e) {
      
    }
    localStorage.setItem(userKey('qais_onboarding'), JSON.stringify({ ...form, completed: true, completedAt: Date.now() }));
    onComplete(form);
    setSaving(false);
  };

  const industries = ['Healthcare & Medical', 'Finance & Banking', 'HR & Recruitment', 'Education', 'Legal & Justice', 'Government', 'Critical Infrastructure', 'Retail & E-commerce', 'Technology', 'Manufacturing', 'Other'];
  const sizes = ['1–10 employees', '11–50 employees', '51–200 employees', '201–1000 employees', '1000+ employees'];
  const goals = ['Achieve ISO 42001 certification', 'EU AI Act compliance', 'NIST AI RMF alignment', 'General AI governance', 'Prepare for audit'];
  const governance = ['None yet', 'Informal practices only', 'Some documented policies', 'Formal framework in progress', 'Established framework'];
  const risks = ['Minimal Risk', 'Limited Risk', 'High Risk', 'Unacceptable / Unsure'];
  const regions = ['EU / Europe', 'United States', 'United Kingdom', 'Asia Pacific', 'Global'];

  const sel = (opts, key) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {opts.map(opt => (
        <div key={opt} onClick={() => update(key, opt)} style={{
          padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
          border: `2px solid ${form[key] === opt ? '#3b82f6' : '#e2e8f0'}`,
          backgroundColor: form[key] === opt ? '#eff6ff' : 'white',
          fontSize: '14px', color: form[key] === opt ? '#1e40af' : '#334155',
          fontWeight: form[key] === opt ? '600' : '400',
          display: 'flex', alignItems: 'center', gap: '10px'
        }}>
          <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${form[key] === opt ? '#3b82f6' : '#cbd5e1'}`, backgroundColor: form[key] === opt ? '#3b82f6' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {form[key] === opt && <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: 'white' }} />}
          </div>
          {opt}
        </div>
      ))}
    </div>
  );

  const steps = [
    {
      title: 'Tell us about your company',
      icon: '🏢',
      content: (
        <>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Company name</label>
            {isEditMode ? (
              <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '14px', color: '#334155', fontWeight: '600' }}>{form.companyName}</span>
                <span style={{ fontSize: '11px', color: '#94a3b8', backgroundColor: '#f1f5f9', padding: '2px 8px', borderRadius: '10px' }}>🔒 Locked</span>
              </div>
            ) : (
              <input value={form.companyName} onChange={e => update('companyName', e.target.value)} placeholder="e.g. Acme AI Solutions" style={inputStyle} />
            )}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Industry</label>
            {isEditMode ? (
              <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '14px', color: '#334155', fontWeight: '600' }}>{form.industry}</span>
                <span style={{ fontSize: '11px', color: '#94a3b8', backgroundColor: '#f1f5f9', padding: '2px 8px', borderRadius: '10px' }}>🔒 Locked</span>
              </div>
            ) : (
              sel(industries, 'industry')
            )}
          </div>
        </>
      ),
      valid: form.companyName.trim() && form.industry
    },
    {
      title: 'Company size & AI system',
      icon: '🤖',
      content: (
        <>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Company size</label>
            {sel(sizes, 'companySize')}
          </div>
          <div style={{ marginBottom: '16px' }}>
           <label style={labelStyle}>AI system name <span style={{ color: '#ef4444' }}>*</span> <span style={{ color: '#94a3b8', fontWeight: '400' }}>(e.g. Customer Service Chatbot)</span></label>
            <input value={form.aiSystemProfile.name} onChange={e => { updateSystem('name', e.target.value); update('aiSystems', e.target.value + (form.aiSystemProfile.purpose ? ' — ' + form.aiSystemProfile.purpose : '')); }} placeholder="e.g. Customer Service Chatbot" style={inputStyle} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>What does it do? <span style={{ color: '#ef4444' }}>*</span></label>
            <textarea value={form.aiSystemProfile.purpose} onChange={e => { updateSystem('purpose', e.target.value); update('aiSystems', (form.aiSystemProfile.name || 'AI System') + ' — ' + e.target.value); }} rows={2} placeholder="e.g. Answers customer questions and routes support tickets automatically" style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          
          
        <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Model / technology type</label>
            {['LLM / Conversational AI', 'Classification / Prediction', 'Computer Vision', 'Recommendation Engine', 'Decision Automation', 'Other'].map(opt => (
              <div key={opt} onClick={() => updateSystem('modelType', opt)} style={{ padding: '8px 12px', marginBottom: '4px', borderRadius: '8px', cursor: 'pointer', border: `2px solid ${form.aiSystemProfile.modelType === opt ? '#3b82f6' : '#e2e8f0'}`, backgroundColor: form.aiSystemProfile.modelType === opt ? '#eff6ff' : 'white', fontSize: '13px', color: form.aiSystemProfile.modelType === opt ? '#1e40af' : '#334155', fontWeight: form.aiSystemProfile.modelType === opt ? '600' : '400' }}>
                {opt}
              </div>
            ))}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Built on an external foundation model? <span style={{ color: '#94a3b8', fontWeight: '400' }}>(EU AI Act Art. 25)</span></label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              {[{label: 'Yes', val: true}, {label: 'No — fully custom', val: false}].map(opt => (
                <div key={opt.label} onClick={() => updateSystem('usesFoundationModel', opt.val)} style={{ flex: 1, padding: '10px 14px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${form.aiSystemProfile.usesFoundationModel === opt.val ? '#3b82f6' : '#e2e8f0'}`, backgroundColor: form.aiSystemProfile.usesFoundationModel === opt.val ? '#eff6ff' : 'white', fontSize: '13px', color: form.aiSystemProfile.usesFoundationModel === opt.val ? '#1e40af' : '#334155', fontWeight: form.aiSystemProfile.usesFoundationModel === opt.val ? '600' : '400', textAlign: 'center' }}>
                  {opt.label}
                </div>
              ))}
            </div>
            {form.aiSystemProfile.usesFoundationModel === true && (
              <select value={form.aiSystemProfile.foundationModel} onChange={e => updateSystem('foundationModel', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">Select foundation model...</option>
                <optgroup label="Anthropic">
                  <option value="anthropic-claude">Claude (Sonnet / Opus / Haiku)</option>
                </optgroup>
                <optgroup label="OpenAI">
                  <option value="openai-gpt4">GPT-4 / GPT-4o</option>
                  <option value="openai-gpt35">GPT-3.5</option>
                </optgroup>
                <optgroup label="Google">
                  <option value="google-gemini">Gemini</option>
                  <option value="google-palm">PaLM (legacy)</option>
                </optgroup>
                <optgroup label="Meta">
                  <option value="meta-llama3">Llama 3.x</option>
                  <option value="meta-llama2">Llama 2 (legacy)</option>
                </optgroup>
                <optgroup label="Other major">
                  <option value="mistral">Mistral / Mixtral</option>
                  <option value="cohere">Cohere Command</option>
                  <option value="ai21">AI21 Jurassic</option>
                  <option value="xai-grok">xAI Grok</option>
                  <option value="inflection">Inflection Pi</option>
                </optgroup>
                <optgroup label="Open source">
                  <option value="stable-diffusion">Stable Diffusion / SDXL (image)</option>
                  <option value="whisper">Whisper (audio)</option>
                  <option value="other-oss">Other open-source LLM</option>
                </optgroup>
                <optgroup label="Custom / proprietary">
                  <option value="custom-foundation">Custom-trained foundation model</option>
                  <option value="multiple">Multiple foundation models</option>
                </optgroup>
                <option value="other">Other (please describe in purpose)</option>
              </select>
            )}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Decision impact level <span style={{ color: '#94a3b8', fontWeight: '400' }}>(if AI makes a mistake)</span></label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[
                { val: 'low', title: 'Low', desc: 'Minor inconvenience, easy to correct' },
                { val: 'medium', title: 'Medium', desc: 'Operational/financial impact, recoverable' },
                { val: 'high', title: 'High', desc: 'Significant harm to individuals, hard to reverse' },
                { val: 'critical', title: 'Critical', desc: 'Serious physical, legal, or financial harm' }
              ].map(opt => (
                <div key={opt.val} onClick={() => updateSystem('impactSeverity', opt.val)} style={{ padding: '10px 14px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${form.aiSystemProfile.impactSeverity === opt.val ? '#3b82f6' : '#e2e8f0'}`, backgroundColor: form.aiSystemProfile.impactSeverity === opt.val ? '#eff6ff' : 'white', fontSize: '13px' }}>
                  <span style={{ fontWeight: '600', color: form.aiSystemProfile.impactSeverity === opt.val ? '#1e40af' : '#334155' }}>{opt.title}</span>
                  <span style={{ marginLeft: '8px', color: '#64748b' }}>— {opt.desc}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Data categories processed <span style={{ color: '#94a3b8', fontWeight: '400' }}>(select all that apply)</span></label>



            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {['Personal data (PII)', 'Health / medical data', 'Financial data', 'Biometric data', 'Employment data', 'Behavioural data', 'No personal data'].map(cat => {
                const selected = (form.aiSystemProfile.dataCategories || []).includes(cat);
                return (
                  <div key={cat} onClick={() => toggleDataCategory(cat)} style={{ padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', border: `2px solid ${selected ? '#3b82f6' : '#e2e8f0'}`, backgroundColor: selected ? '#eff6ff' : 'white', fontSize: '13px', color: selected ? '#1e40af' : '#64748b', fontWeight: selected ? '600' : '400' }}>
                    {cat}
                  </div>
                );
              })}
            </div>
          </div>
          
          
          
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Who is affected by it? <span style={{ color: '#94a3b8', fontWeight: '400' }}>(select all that apply)</span></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {[
                'Customers / external users (B2C)',
                'Business clients / partners (B2B)',
                'Employees / internal staff',
                'Job candidates / applicants',
                'Patients (healthcare)',
                'Students / learners',
                'General public',
                'Vulnerable populations (children, elderly, etc.)',
                'Other'
              ].map(group => {
                const selected = (form.aiSystemProfile.affectedUsers || []).includes(group);
                return (
                  <div key={group} onClick={() => toggleAffectedUser(group)} style={{ padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', border: `2px solid ${selected ? '#3b82f6' : '#e2e8f0'}`, backgroundColor: selected ? '#eff6ff' : 'white', fontSize: '13px', color: selected ? '#1e40af' : '#64748b', fontWeight: selected ? '600' : '400' }}>
                    {group}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
             <label style={labelStyle}>Lifecycle stage <span style={{ color: '#ef4444' }}>*</span></label>
              <select value={form.aiSystemProfile.lifecycleStage} onChange={e => updateSystem('lifecycleStage', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                 <option value="">-- Select stage --</option>
                {['Design', 'Development', 'Testing', 'Deployed', 'Retired'].map(s => <option key={s} value={s.toLowerCase()}>{s}</option>)}
              </select>
            </div>
            <div>
              {/* Empty cell to keep Lifecycle stage at half width */}
            </div>
          </div>


          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Business owner <span style={{ color: '#94a3b8', fontWeight: '400' }}>(optional)</span></label>
              <input value={form.aiSystemProfile.businessOwner} onChange={e => updateSystem('businessOwner', e.target.value)} placeholder="e.g. Head of Product" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Technical owner <span style={{ color: '#94a3b8', fontWeight: '400' }}>(optional)</span></label>
              <input value={form.aiSystemProfile.technicalOwner} onChange={e => updateSystem('technicalOwner', e.target.value)} placeholder="e.g. CTO / Lead Engineer" style={inputStyle} />
            </div>
          </div>
        </>
      ),
      
      valid: form.companySize 
        && form.aiSystemProfile.name.trim()
        && form.aiSystemProfile.purpose.trim()
        && form.aiSystemProfile.modelType
        && form.aiSystemProfile.usesFoundationModel !== ''
        && (form.aiSystemProfile.usesFoundationModel === false || form.aiSystemProfile.foundationModel)
        && form.aiSystemProfile.impactSeverity
        && Array.isArray(form.aiSystemProfile.affectedUsers) && form.aiSystemProfile.affectedUsers.length > 0
        && form.aiSystemProfile.lifecycleStage

    },
    {
      title: 'Compliance goals & current state',
      icon: '🎯',
      content: (
        <>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Primary compliance goal</label>
            {sel(goals, 'primaryGoal')}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Current AI governance maturity</label>
            {sel(governance, 'currentGovernance')}
          </div>
        </>
      ),
      valid: form.primaryGoal && form.currentGovernance
    },


  {
      title: 'Risk & regulatory profile',
      icon: '⚖️',
      content: (
        <>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Estimated EU AI Act risk level for your AI</label>
            {sel(risks, 'euAiRisk')}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Where do you deploy your AI systems?</label>
            {sel(regions, 'deploymentRegions')}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Human oversight measures <span style={{ color: '#94a3b8', fontWeight: '400' }}>(EU AI Act Art. 14 — select all that apply)</span></label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[
                { val: 'review_before',   label: 'Human reviews AI output before action is taken' },
                { val: 'periodic_audit',  label: 'Periodic human audit of AI decisions' },
                { val: 'override',        label: 'Clear way for humans to override AI' },
                { val: 'realtime_loop',   label: 'Real-time human-in-the-loop monitoring' },
                { val: 'fully_autonomous',label: 'None — fully autonomous (no human oversight)' }
              ].map(opt => {
                const selected = (form.aiSystemProfile.humanOversight || []).includes(opt.val);
                return (
                  <div key={opt.val} onClick={() => {
                    const current = form.aiSystemProfile.humanOversight || [];
                    let updated;
                    // "fully autonomous" is mutually exclusive with everything else
                    if (opt.val === 'fully_autonomous') {
                      updated = selected ? [] : ['fully_autonomous'];
                    } else if (current.includes('fully_autonomous')) {
                      updated = [opt.val];
                    } else {
                      updated = selected ? current.filter(v => v !== opt.val) : [...current, opt.val];
                    }
                    updateSystem('humanOversight', updated);
                  }} style={{ padding: '10px 14px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${selected ? '#3b82f6' : '#e2e8f0'}`, backgroundColor: selected ? '#eff6ff' : 'white', fontSize: '13px', color: selected ? '#1e40af' : '#334155', fontWeight: selected ? '600' : '400' }}>
                    <span style={{ marginRight: '10px' }}>{selected ? '☑' : '☐'}</span>
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ),
      valid: form.euAiRisk && form.deploymentRegions && (form.aiSystemProfile.humanOversight || []).length > 0
    }

  ];

  const currentStep = steps[step - 1];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
      <div style={{ width: '100%', maxWidth: '600px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>{currentStep.icon}</div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#0f172a', marginBottom: '6px' }}>
            Set up your compliance profile
          </h1>
          <p style={{ fontSize: '15px', color: '#64748b' }}>
            Step {step} of {steps.length} — takes about 2 minutes
          </p>
        </div>

        {/* Progress */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '28px' }}>
          {steps.map((_, i) => (
            <div key={i} style={{ flex: 1, height: '4px', borderRadius: '4px', backgroundColor: i < step ? '#3b82f6' : '#e2e8f0', transition: 'background-color 0.3s' }} />
          ))}
        </div>

        {/* Card */}
        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px', padding: '28px', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', marginBottom: '20px' }}>
            {currentStep.title}
          </h2>
          {currentStep.content}
        </div>

        {/* Nav */}
        <div style={{ display: 'flex', gap: '12px' }}>
          {step > 1 && (
            <button onClick={() => setStep(p => p - 1)} style={{ padding: '14px 24px', backgroundColor: 'white', color: '#334155', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
              ← Back
            </button>
          )}
          {step < steps.length ? (
            <button
              onClick={() => currentStep.valid && setStep(p => p + 1)}
              disabled={!currentStep.valid}
              style={{ flex: 1, padding: '14px', backgroundColor: currentStep.valid ? '#3b82f6' : '#cbd5e1', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: currentStep.valid ? 'pointer' : 'not-allowed' }}
            >
              Continue →
            </button>
          ) : (
            <button
              onClick={() => currentStep.valid && handleComplete()}
              disabled={!currentStep.valid || saving}
              style={{ flex: 1, padding: '14px', backgroundColor: currentStep.valid ? '#10b981' : '#cbd5e1', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: currentStep.valid ? 'pointer' : 'not-allowed' }}
            >
              {saving ? '⏳ Setting up...' : '🚀 Launch My Dashboard'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Score calculator from onboarding data ──
function calculateScores(data) {
  if (!data) {
    const c = (ck) => ck?.categories?.reduce((s, cat) => s + (cat.items?.length || 0), 0) || 0;
    return {
      iso: 0, eu: 0, nist: 0,
      isoTotal:  c(complianceChecklists.iso42001_checklist),
      euTotal:   c(complianceChecklists.eu_ai_act_high_risk_checklist),
      nistTotal: c(complianceChecklists.nist_ai_rmf_checklist),
    };
  }

  const governanceMap = { 'None yet': 0, 'Informal practices only': 0.1, 'Some documented policies': 0.25, 'Formal framework in progress': 0.45, 'Established framework': 0.75 };
  const riskMap = { 'Minimal Risk': 0.6, 'Limited Risk': 0.35, 'High Risk': 0.15, 'Unacceptable / Unsure': 0.05 };
  const goalBoost = { 'Achieve ISO 42001 certification': { iso: 0.15 }, 'EU AI Act compliance': { eu: 0.15 }, 'NIST AI RMF alignment': { nist: 0.15 }, 'General AI governance': {}, 'Prepare for audit': { iso: 0.1, eu: 0.1, nist: 0.1 } };

  const govScore = governanceMap[data.currentGovernance] ?? 0.1;
  const riskScore = riskMap[data.euAiRisk] ?? 0.2;
  const boost = goalBoost[data.primaryGoal] ?? {};

  // Read actual item counts from ComplianceChecklists.json — no hardcoded mismatch
  const countItems = (checklist) => {
    if (!checklist?.categories) return 0;
    return checklist.categories.reduce((sum, cat) => sum + (cat.items?.length || 0), 0);
  };
  const isoTotal  = countItems(complianceChecklists.iso42001_checklist);
  const euTotal   = countItems(complianceChecklists.eu_ai_act_high_risk_checklist);
  const nistTotal = countItems(complianceChecklists.nist_ai_rmf_checklist);


  const base = govScore * 0.7 + riskScore * 0.3;

  const isoPercent = Math.min(Math.round((base + (boost.iso || 0)) * 100), 95);
  const euPercent = Math.min(Math.round((base * 0.9 + (boost.eu || 0)) * 100), 95);
  const nistPercent = Math.min(Math.round((base * 0.85 + (boost.nist || 0)) * 100), 95);

  return {
    iso: Math.round((isoPercent / 100) * isoTotal),
    eu: Math.round((euPercent / 100) * euTotal),
    nist: Math.round((nistPercent / 100) * nistTotal),
    isoTotal, euTotal, nistTotal,
    isoPercent, euPercent, nistPercent
  };
}

const labelStyle = { display: 'block', fontWeight: '600', color: '#334155', marginBottom: '8px', fontSize: '14px' };
const inputStyle = { width: '100%', padding: '12px 14px', fontSize: '14px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };


// ════════════════════════════════════════
// MAIN DASHBOARD
// ════════════════════════════════════════
// ── Fallback deadlines (used if Supabase fetch fails) ──
const FALLBACK_DEADLINES = [
  { date: 'Feb 2, 2025',  label: 'Prohibited Practices + AI Literacy',        ref: 'Article 5 + Article 4', status: 'past',      urgent: false, date_iso: '2025-02-02', fine_specific: null,               fine_max: '€35M or 7% global turnover' },
  { date: 'Aug 2, 2025',  label: 'GPAI Obligations + Penalties Framework',     ref: 'Articles 51–56',        status: 'past',     urgent: false, date_iso: '2025-08-02', fine_specific: '€15M or 3% turnover', fine_max: '€35M or 7% global turnover' },
  { date: 'Aug 2, 2026',  label: 'Transparency Obligations + Full Enforcement',ref: 'Articles 50–52',        status: 'past',     urgent: false,  date_iso: '2026-08-02', fine_specific: '€15M or 3% turnover', fine_max: '€35M or 7% global turnover' },
  { date: 'Dec 2, 2027',  label: 'High-Risk AI Systems (Standalone)',          ref: 'Articles 9–15 (Annex III)', status: 'upcoming', urgent: true, date_iso: '2027-12-02', fine_specific: '€15M or 3% turnover', fine_max: '€35M or 7% global turnover' },
  { date: 'Aug 2, 2028',  label: 'High-Risk AI in Regulated Products',         ref: 'Articles 9–15 (Annex I)',   status: 'upcoming', urgent: false, date_iso: '2028-08-02', fine_specific: '€15M or 3% turnover', fine_max: '€35M or 7% global turnover' },
];

function Dashboard() {
  const [onboarding, setOnboarding] = useState(null);
  const [actionStates, setActionStates] = useState(Array(20).fill(false));
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [docsGenerated, setDocsGenerated] = useState(0);
  const [lastUpdated] = useState(new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
  const [userEmail, setUserEmail] = useState('');
  const [deadlines, setDeadlines] = useState(FALLBACK_DEADLINES);
  const [deadlinesLoading, setDeadlinesLoading] = useState(true);
  const [estTime, setEstTime] = useState('');

  useEffect(() => {
    const local = localStorage.getItem(userKey('qais_onboarding'));
    if (local) {
      try {
        const parsed = JSON.parse(local);
        if (parsed.completed) { setOnboarding(parsed); setLoading(false); return; }
      } catch {}
    }
    const checkSupabase = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data } = await supabase.from('user_profiles').select('onboarding_data, onboarding_completed').eq('user_id', user.id).single();
          if (data?.onboarding_completed) {
            setOnboarding(data.onboarding_data);
            localStorage.setItem(userKey('qais_onboarding'), JSON.stringify({ ...data.onboarding_data, completed: true }));
          }
        }
      } catch {}
      setLoading(false);
    };
    checkSupabase();
  }, []);


  // Load saved action states for current risk level
  useEffect(() => {
    if (!onboarding) return;
    const rk = { 'Minimal Risk': 'minimal', 'Limited Risk': 'limited', 'High Risk': 'high', 'Unacceptable / Unsure': 'unacceptable' }[onboarding.euAiRisk] || 'high';
    try {
      const saved = localStorage.getItem(userKey(`qais_action_states_${rk}`));
      if (saved) {
        const parsed = JSON.parse(saved);
        while (parsed.length < 20) parsed.push(false);
        setActionStates(parsed);
      } else {
        setActionStates(Array(20).fill(false));
      }
    } catch {
      setActionStates(Array(20).fill(false));
    }
  }, [onboarding]);

  // Load accurate doc count from Supabase on mount
  useEffect(() => {
    getDocCount().then(count => setDocsGenerated(count));
  }, []);

  // Fetch and store user email for localStorage key prefixing
  useEffect(() => {
    const fetchUserEmail = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
        localStorage.setItem('qais_user_email', user.email);
      }
    };
    fetchUserEmail();
  }, []);

  // ── Fetch compliance deadlines from Supabase ──
  // Falls back to FALLBACK_DEADLINES if table doesn't exist or fetch fails.
  // To add a new framework (NIST, ISO 42001, etc.), just INSERT rows in Supabase —
  // no code changes needed.
  useEffect(() => {

// Overrides each row's status/urgent with a value freshly computed from
  // date_iso, so a deadline flips to "past"/"in force" automatically the
  // moment it passes — no manual DB or code edit ever needed again.
  const withComputedStatus = (rows) => rows.map(row => {
    if (!row.date_iso) return row;
    const computed = getDeadlineStatus(row.date_iso);
    return {
      ...row,
      status: computed.status === 'in-force' ? 'past' : 'upcoming',
      urgent: computed.status === 'urgent',
    };
  });


    const fetchDeadlines = async () => {
      try {
        const { data, error } = await supabase
          .from('compliance_deadlines')
          .select('milestone, date_label, date_iso, article_ref, status, urgent, fine_specific, fine_max, framework')
          .eq('active', true)
          .order('sort_order', { ascending: true });

        if (error || !data || data.length === 0) {
          // Table not yet created or empty — use fallback silently
          setDeadlines(withComputedStatus(FALLBACK_DEADLINES));
        } else {
          // Normalize Supabase rows to match the shape used in the render.
          // status/urgent are RECOMPUTED from date_iso here, not trusted from
          // the DB columns — those columns can go stale the moment a deadline
          // passes if nobody remembers to update them by hand.
          const normalized = withComputedStatus(data.map(row => ({
            date:         row.date_label,
            label:        row.milestone,
            ref:          row.article_ref || '',
            status:       row.status,
            urgent:       row.urgent,
            date_iso:     row.date_iso,
            fine_specific: row.fine_specific,
            fine_max:     row.fine_max,
            framework:    row.framework,
          })));
          setDeadlines(normalized);
        }
      } catch {
        // Network error or table missing — fall back gracefully
        setDeadlines(withComputedStatus(FALLBACK_DEADLINES));
      } finally {
        setDeadlinesLoading(false);
      }
    };
    fetchDeadlines();
  }, []);

  // ── Live EST clock — updates every second ──
  useEffect(() => {
    const formatEST = () => {
      const now = new Date();
      return now.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      }) + ' EST';
    };
    setEstTime(formatEST());
    const timer = setInterval(() => setEstTime(formatEST()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleOnboardingComplete = (data) => setOnboarding(data);

  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [editConfirmChecked, setEditConfirmChecked] = useState(false);


  const handleEditProfile = () => {
    setEditConfirmChecked(false);
    setShowEditConfirm(true);
  };

  const confirmEditProfile = () => {
    // Clear downstream data that depends on companyName + industry
    // companyName and industry will be locked — only other fields can change
    localStorage.removeItem(userKey('qais_risk_result'));
    localStorage.removeItem(userKey('qais_checklist_setup'));
    localStorage.removeItem(userKey('qais_checklist_progress'));
    localStorage.removeItem(userKey('qais_eu_classifier_result'));
    localStorage.removeItem(userKey('qais_action_states_minimal'));
    localStorage.removeItem(userKey('qais_action_states_limited'));
    localStorage.removeItem(userKey('qais_action_states_high'));
    localStorage.removeItem(userKey('qais_action_states_unacceptable'));
    setShowEditConfirm(false);
    setEditConfirmChecked(false);
    // Pass existing onboarding as lockedData so companyName + industry stay locked
    setOnboarding(null);
    setEditingProfile(true);
  };

  const [editingProfile, setEditingProfile] = useState(false);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px' }}>
        <div style={{ fontSize: '40px' }}>⏳</div>
        <p style={{ color: '#64748b', fontSize: '16px' }}>Loading your compliance profile...</p>
      </div>
    );
  }

  if (!onboarding) {
    return (
      <OnboardingForm
        onComplete={(data) => { handleOnboardingComplete(data); setEditingProfile(false); }}
        lockedData={editingProfile ? (() => { try { return JSON.parse(localStorage.getItem(userKey('qais_onboarding')) || '{}'); } catch { return null; } })() : null}
      />
    );
  }

  const scores = calculateScores(onboarding);
  const overallPercent = Math.round(((scores.iso + scores.eu + scores.nist) / (scores.isoTotal + scores.euTotal + scores.nistTotal)) * 100);

  // ── Framework ordering based on primary goal ──
  const goalToFramework = {
    'Achieve ISO 42001 certification': 'iso',
    'EU AI Act compliance': 'eu',
    'NIST AI RMF alignment': 'nist',
    'General AI governance': null,
    'Prepare for audit': null,
  };
  const primaryFramework = goalToFramework[onboarding.primaryGoal] || null;

  const allFrameworks = [
    { key: 'iso', label: 'ISO 42001', icon: '📘', color: '#3b82f6', bg: '#eff6ff', pct: scores.isoPercent, items: scores.iso, total: scores.isoTotal,
      whyMatters: 'ISO 42001 is the global standard for AI management systems and directly supports your EU AI Act and NIST compliance — achieving it satisfies requirements across all three frameworks simultaneously.' },
    { key: 'eu', label: 'EU AI Act', icon: '🇪🇺', color: '#8b5cf6', bg: '#faf5ff', pct: scores.euPercent, items: scores.eu, total: scores.euTotal,
      whyMatters: 'The EU AI Act is legally binding for organizations operating in or selling to the EU. Even if it is not your primary goal, non-compliance risks fines up to €15M or 3% of global turnover.' },
    { key: 'nist', label: 'NIST AI RMF', icon: '🏛️', color: '#06b6d4', bg: '#ecfeff', pct: scores.nistPercent, items: scores.nist, total: scores.nistTotal,
      whyMatters: 'NIST AI RMF is the leading US voluntary framework and increasingly required by US federal agencies and enterprise clients. Its GOVERN, MAP, MEASURE, MANAGE functions complement ISO 42001 directly.' },
  ];

  // Sort: primary goal framework first, others after
  const orderedFrameworks = primaryFramework
    ? [ ...allFrameworks.filter(f => f.key === primaryFramework), ...allFrameworks.filter(f => f.key !== primaryFramework) ]
    : allFrameworks;

  const primaryFw = orderedFrameworks[0];
  const secondaryFws = orderedFrameworks.slice(1);

  const getColor = (p) => p >= 70 ? '#10b981' : p >= 40 ? '#f59e0b' : '#ef4444';
  const getRiskColor = (risk) => ({ 'Minimal Risk': '#10b981', 'Limited Risk': '#3b82f6', 'High Risk': '#f59e0b', 'Unacceptable / Unsure': '#ef4444' }[risk] || '#64748b');
  const getRiskIcon = (risk) => ({ 'Minimal Risk': '✅', 'Limited Risk': 'ℹ️', 'High Risk': '⚠️', 'Unacceptable / Unsure': '🚫' }[risk] || '❓');

  const mockHistory = [
    { month: 'Oct', iso: Math.max(scores.isoPercent - 22, 5), eu: Math.max(scores.euPercent - 20, 5), nist: Math.max(scores.nistPercent - 18, 5) },
    { month: 'Nov', iso: Math.max(scores.isoPercent - 15, 5), eu: Math.max(scores.euPercent - 13, 5), nist: Math.max(scores.nistPercent - 12, 5) },
    { month: 'Dec', iso: Math.max(scores.isoPercent - 9, 5), eu: Math.max(scores.euPercent - 8, 5), nist: Math.max(scores.nistPercent - 7, 5) },
    { month: 'Jan', iso: Math.max(scores.isoPercent - 5, 5), eu: Math.max(scores.euPercent - 4, 5), nist: Math.max(scores.nistPercent - 3, 5) },
    { month: 'Feb', iso: scores.isoPercent, eu: scores.euPercent, nist: scores.nistPercent },
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'frameworks', label: 'Frameworks', icon: '📋' },
    { id: 'timeline', label: 'Timeline', icon: '📅' },
    { id: 'actions', label: 'Actions', icon: '✅' },
	{ id: 'ai-decisions', label: 'AI Decisions', icon: '🤖' },
  ];

  // ── Dynamic actions from JSON based on EU AI Act risk level ──
  const euRiskToKey = {
    'Minimal Risk': 'minimal',
    'Limited Risk': 'limited',
    'High Risk': 'high',
    'Unacceptable / Unsure': 'unacceptable',
  };
  const riskKey = euRiskToKey[onboarding.euAiRisk] || 'high';

  // Build actions from EU AI Act classifier JSON — primary source
  const euObligations = (classifierData.risk_levels[riskKey]?.obligations || []).map(o => ({
    priority: o.urgent ? 'critical' : 'medium',
    text: o.title,
    framework: o.ref,
    deadline: o.urgent ? 'Urgent — act now' : 'Within 6 months',
  }));

  // Build actions from risk calculator JSON — supplement only
  const riskActions = (riskData.compliance_actions[riskKey] || []).map(o => ({
    priority: o.priority,
    text: o.action,
    framework: o.framework,
    deadline: o.deadline,
  }));

  // Smart deduplication — extract keywords and skip near-duplicates
  // Common words that appear in many compliance actions — ignore these for deduplication
  const stopWords = new Set(['system', 'management', 'risk', 'artificial', 'intelligence', 'ensure', 'establish', 'implement', 'provide', 'conduct', 'create', 'define', 'develop', 'assess', 'review', 'maintain', 'monitor', 'document', 'based', 'organization', 'requirements', 'process', 'practices', 'activities', 'information', 'measures', 'compliance', 'policies']);

  const extractKeywords = (text) => text.toLowerCase()
    .replace(/[^a-z\s]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 5 && !stopWords.has(w))
    .sort();

  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  const seenKeywords = [];

  const complianceActions = [...euObligations, ...riskActions]
    .filter(a => {
      const kw = extractKeywords(a.text);
      // Only deduplicate if 3+ rare keywords match (not common compliance words)
      for (const seen of seenKeywords) {
        const aWords = new Set(kw);
        const shared = seen.filter(w => aWords.has(w)).length;
        if (shared >= 3) return false;
      }
      seenKeywords.push(kw);
      return true;
    })
    .sort((a, b) => {
      const pa = priorityOrder[a.priority] ?? 99;
      const pb = priorityOrder[b.priority] ?? 99;
      return pa - pb;
    });

  const completedActions = actionStates.filter(Boolean).length;
  const actionProgress = Math.round((completedActions / complianceActions.length) * 100);

  // Dynamic — fetched from Supabase compliance_deadlines table.
  // Falls back to FALLBACK_DEADLINES if unavailable.
  // To add NIST, ISO 42001, or any future framework: INSERT rows in Supabase only.
  // Sorted by days remaining ascending (soonest first — past items last)
  const enforcementDates = [...deadlines].sort((a, b) => {
    const aDate = a.date_iso ? new Date(a.date_iso) : new Date('9999-12-31');
    const bDate = b.date_iso ? new Date(b.date_iso) : new Date('9999-12-31');
    return aDate - bDate;
  });

  // Build per-deadline countdown data — sorted by days remaining ascending
  // past: show "In Force" grayed out — no countdown
  // upcoming: show live days remaining calculated from today on every render
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const countdownItems = [...deadlines]
    .map(d => {
      const target = d.date_iso ? new Date(d.date_iso) : null;
      const isPast = target ? target < today : d.status === 'past';
      const daysLeft = target
        ? Math.max(0, Math.ceil((target - today) / (1000 * 60 * 60 * 24)))
        : null;
      return { ...d, isPast, daysLeft };
    })
    .sort((a, b) => {
      // Past (In Force) items go to TOP, sorted ascending by date (oldest first)
      if (a.isPast && !b.isPast) return -1;
      if (!a.isPast && b.isPast) return 1;
      // Both past — sort ascending (Feb 2025 before Aug 2025)
      if (a.isPast && b.isPast) return new Date(a.date_iso) - new Date(b.date_iso);
      // Both upcoming — sort ascending by days (soonest first)
      return (a.daysLeft ?? 9999) - (b.daysLeft ?? 9999);
    });

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '32px 24px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
                {onboarding.companyName ? `${onboarding.companyName}'s` : 'Your'} Compliance Dashboard
              </h1>
              <span style={{ fontSize: '12px', backgroundColor: '#dcfce7', color: '#15803d', padding: '3px 10px', borderRadius: '20px', fontWeight: '600' }}>LIVE</span>
            </div>
            <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
              {onboarding.industry} · {onboarding.companySize} · Last updated {lastUpdated}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', padding: '8px 14px', borderRadius: '10px', backgroundColor: 'white', border: '2px solid #e2e8f0', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {getRiskIcon(onboarding.euAiRisk)} {onboarding.euAiRisk}
            </span>
            <button onClick={handleEditProfile} style={{ fontSize: '13px', padding: '8px 14px', borderRadius: '10px', backgroundColor: 'white', border: '2px solid #e2e8f0', color: '#64748b', cursor: 'pointer' }}>
              ⚙️ Edit Profile
            </button>

            {/* ── Edit Profile Confirmation Dialog ── */}
            {showEditConfirm && (
              <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '28px', maxWidth: '460px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                  <div style={{ fontSize: '28px', marginBottom: '12px', textAlign: 'center' }}>⚙️</div>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 8px', textAlign: 'center' }}>Edit Your Profile</h3>
                  <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 16px', textAlign: 'center', lineHeight: '1.6' }}>
                    You can update your AI system details, compliance goals, and governance settings.
                  </p>
                  <div style={{ backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '700', color: '#92400e', margin: '0 0 6px' }}>⚠️ The following will be reset:</p>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#92400e', lineHeight: '1.8' }}>
                      <li>Risk Score results</li>
                      <li>EU AI Act classification</li>
                      <li>Checklist setup and progress</li>
                      <li>Dashboard action states</li>
                    </ul>
                  </div>
                  <div style={{ backgroundColor: '#f0fdf4', border: '2px solid #86efac', borderRadius: '10px', padding: '14px', marginBottom: '20px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '700', color: '#15803d', margin: '0 0 6px' }}>🔒 These will stay locked:</p>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#15803d', lineHeight: '1.8' }}>
                      <li>Company name — <strong>{onboarding.companyName}</strong></li>
                      <li>Industry — <strong>{onboarding.industry}</strong></li>
                    </ul>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '20px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={editConfirmChecked} onChange={e => setEditConfirmChecked(e.target.checked)} style={{ marginTop: '2px', width: '16px', height: '16px', cursor: 'pointer' }} />
                    <span style={{ fontSize: '13px', color: '#334155', lineHeight: '1.5' }}>
                      I understand my Risk Score, EU Classifier, and Checklist results will be cleared and I will need to redo them.
                    </span>
                  </label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => setShowEditConfirm(false)} style={{ flex: 1, padding: '12px', backgroundColor: 'white', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                      Cancel
                    </button>
                    <button
                      onClick={confirmEditProfile}
                      disabled={!editConfirmChecked}
                      style={{ flex: 1, padding: '12px', backgroundColor: editConfirmChecked ? '#3b82f6' : '#cbd5e1', color: 'white', border: 'none', borderRadius: '10px', cursor: editConfirmChecked ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '600', transition: 'background 0.2s' }}
                    >
                      Continue to Edit
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── KPI Row ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginBottom: '24px' }}>
          {[
            { label: 'Overall Score', value: overallPercent, suffix: '%', color: getColor(overallPercent), sparkline: mockHistory.map(h => Math.round((h.iso + h.eu + h.nist) / 3)), icon: '🛡️' },
            ...orderedFrameworks.map(fw => ({ label: fw.label, value: fw.pct, suffix: '%', color: fw.color, sparkline: mockHistory.map(h => h[fw.key]), icon: fw.icon })),
            { label: 'Actions Done', value: completedActions, suffix: `/${complianceActions.length}`, color: '#10b981', sparkline: null, icon: '✅' },
            { label: 'Docs Generated', value: docsGenerated, suffix: '', color: '#f59e0b', sparkline: null, icon: '📄', link: 'documents' },
          ].map((kpi) => (
            <div key={kpi.label}
              onMouseEnter={() => setHoveredCard(kpi.label)}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={() => kpi.link && typeof window !== 'undefined' && document.dispatchEvent(new CustomEvent('navigate', { detail: kpi.link }))}
              style={{ backgroundColor: 'white', border: `2px solid ${hoveredCard === kpi.label ? kpi.color : '#e2e8f0'}`, borderRadius: '14px', padding: '16px', transition: 'border-color 0.2s, transform 0.2s', transform: hoveredCard === kpi.label ? 'translateY(-2px)' : 'none', cursor: kpi.link ? 'pointer' : 'default' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <span style={{ fontSize: '18px' }}>{kpi.icon}</span>
                {kpi.sparkline && <Sparkline values={kpi.sparkline} color={kpi.color} />}
              </div>
              <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>{kpi.label}</p>
              <p style={{ fontSize: '26px', fontWeight: 'bold', color: kpi.color, margin: 0 }}>
                <AnimatedNumber value={kpi.value} suffix={kpi.suffix} />
              </p>
              {kpi.label === 'Docs Generated' && (
                <p style={{ fontSize: '11px', color: '#94a3b8', margin: '4px 0 0' }}>
                  {docsGenerated === 0 ? 'None yet — go to Documents' : 'via Document Generator'}
                </p>
              )}
              {kpi.label === 'Actions Done' && (
                <p style={{ fontSize: '11px', color: '#94a3b8', margin: '4px 0 0' }}>
                  Click Actions tab to update
                </p>
              )}
            </div>
          ))}
        </div>

        {/* ── Tabs ── */}
        <div style={{ display: 'flex', gap: '4px', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '4px', marginBottom: '24px', width: 'fit-content' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', backgroundColor: activeTab === tab.id ? '#3b82f6' : 'transparent', color: activeTab === tab.id ? 'white' : '#64748b', fontSize: '14px', fontWeight: activeTab === tab.id ? '600' : '400', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.15s' }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════ */}
        {/* OVERVIEW TAB */}
        {/* ══════════════════════════════ */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>

            {/* ── Primary Framework Hero Card ── */}
            <div style={{ backgroundColor: 'white', border: `3px solid ${primaryFw.color}`, borderRadius: '16px', padding: '28px', gridColumn: 'span 2' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '24px' }}>{primaryFw.icon}</span>
                    <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>{primaryFw.label}</h2>
                    <span style={{ fontSize: '11px', backgroundColor: primaryFw.color, color: 'white', padding: '3px 10px', borderRadius: '20px', fontWeight: '700' }}>PRIMARY GOAL</span>
                  </div>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>{primaryFw.items} of {primaryFw.total} requirements addressed</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', color: primaryFw.color, lineHeight: 1 }}>
                    <AnimatedNumber value={primaryFw.pct} suffix="%" />
                  </div>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0' }}>readiness score</p>
                </div>
              </div>
              <div style={{ height: '14px', backgroundColor: primaryFw.bg, borderRadius: '7px', overflow: 'hidden', marginBottom: '20px' }}>
                <div style={{ width: `${primaryFw.pct}%`, height: '100%', backgroundColor: primaryFw.color, borderRadius: '7px', transition: 'width 1.2s ease' }} />
              </div>

              {/* Overall score row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: '10px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#64748b' }}>🛡️ Overall compliance score across all frameworks</span>
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  {orderedFrameworks.map(fw => (
                    <div key={fw.key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: fw.color, opacity: fw.key === primaryFw.key ? 1 : 0.4 }} />
                      <span style={{ fontSize: '12px', color: fw.key === primaryFw.key ? fw.color : '#94a3b8', fontWeight: fw.key === primaryFw.key ? '700' : '400' }}>{fw.label} {fw.pct}%</span>
                    </div>
                  ))}
                  <span style={{ fontSize: '16px', fontWeight: 'bold', color: getColor(overallPercent) }}>{overallPercent}% overall</span>
                </div>
              </div>
            </div>

            {/* ── Radial Rings — primary large, secondaries smaller ── */}
            <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 20px' }}>Framework Readiness</h3>
              <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                <RadialRing percent={primaryFw.pct} color={primaryFw.color} size={120} label={primaryFw.label} sublabel={`${primaryFw.items}/${primaryFw.total} items`} />
                {secondaryFws.map(fw => (
                  <div key={fw.key} style={{ opacity: 0.65 }}>
                    <RadialRing percent={fw.pct} color={fw.color} size={84} label={fw.label} sublabel={`${fw.items}/${fw.total}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* Profile Summary */}
            <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px', padding: '24px', gridColumn: 'span 2', gridRow: 'span 3' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 16px' }}>Your Compliance Profile</h3>
{(() => {
                const sys = onboarding.aiSystemProfile || {};
                const cs  = onboarding.currentState     || {};
                // Friendly labels for foundation model values (matches the onboarding select)
                const foundationLabels = {
                  'anthropic-claude': 'Claude (Sonnet/Opus/Haiku)',
                  'openai-gpt4': 'GPT-4 / GPT-4o',
                  'openai-gpt35': 'GPT-3.5',
                  'google-gemini': 'Gemini',
                  'google-palm': 'PaLM (legacy)',
                  'meta-llama3': 'Llama 3.x',
                  'meta-llama2': 'Llama 2 (legacy)',
                  'mistral': 'Mistral / Mixtral',
                  'cohere': 'Cohere Command',
                  'ai21': 'AI21 Jurassic',
                  'xai-grok': 'xAI Grok',
                  'inflection': 'Inflection Pi',
                  'stable-diffusion': 'Stable Diffusion / SDXL',
                  'whisper': 'Whisper (audio)',
                  'other-oss': 'Open-source LLM',
                  'custom-foundation': 'Custom-trained',
                  'multiple': 'Multiple models',
                  'other': 'Other',
                };
                const fmt = (v) => {
                  if (v === undefined || v === null || v === '')   return null;
                  if (Array.isArray(v)) return v.length ? v.join(', ') : null;
                  if (typeof v === 'boolean') return v ? '✓ Yes' : '✗ No';
                  if (typeof v === 'string') {
                    // capitalize first letter for short enum-like values
                    if (v.length <= 12 && v === v.toLowerCase()) return v.charAt(0).toUpperCase() + v.slice(1);
                    return v;
                  }
                  return String(v);
                };

                const sections = [
                  {
                    header: '📋 Company Info',
                    items: [
                      { label: 'Industry',          value: fmt(onboarding.industry),          icon: '🏢' },
                      { label: 'Company size',      value: fmt(onboarding.companySize),       icon: '👥' },
                      { label: 'Deployment region', value: fmt(onboarding.deploymentRegions), icon: '🌍' },
                    ],
                  },
                  {
                    header: '🤖 AI System',
                    items: [
                      { label: 'AI System',       value: fmt(sys.name),                          icon: '🤖' },
                      { label: 'Model type',      value: fmt(sys.modelType),                     icon: '🧠' },
                      { label: 'Foundation model', value: foundationLabels[sys.foundationModel] || fmt(sys.foundationModel), icon: '⚡' },
                      { label: 'Impact severity', value: fmt(sys.impactSeverity),                icon: '⚠️' },
                      { label: 'Lifecycle stage', value: fmt(sys.lifecycleStage),                icon: '🔄' },
                      { label: 'Data categories', value: fmt(sys.dataCategories),                icon: '🔒' },
                      { label: 'Affected users',  value: fmt(sys.affectedUsers),                 icon: '👤' },
                    ],
                  },
                  {
                    header: '🎯 Compliance Posture',
                    items: [
                      { label: 'Primary goal',         value: fmt(onboarding.primaryGoal),       icon: '🎯' },
                      { label: 'Governance maturity', value: fmt(onboarding.currentGovernance), icon: '📈' },
                      { label: 'EU AI risk',           value: fmt(onboarding.euAiRisk),          icon: '🇪🇺' },
                      { label: 'Human oversight',      value: fmt(sys.humanOversight),           icon: '👁️' },
                    ],
                  },
                  {
                    header: '✓ Current State Snapshot',
                    items: [
                      { label: 'AI policy',         value: fmt(cs.hasPolicy),           icon: '📜' },
                      { label: 'Risk register',     value: fmt(cs.hasRiskRegister),     icon: '📋' },
                      { label: 'Bias testing',      value: fmt(cs.doesBiasTest),        icon: '⚖️' },
                      { label: 'Decision logging',  value: fmt(cs.logsDecisions),       icon: '📊' },
                      { label: 'Human review',      value: fmt(cs.requiresHumanReview), icon: '👁️' },
                    ],
                  },
                ];


            return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {sections.map(section => {
                      const visibleItems = section.items.filter(it => it.value !== null && it.value !== undefined);
                      if (visibleItems.length === 0) return null;
                      return (
                        <div key={section.header} style={{
                          backgroundColor: '#f0fdfa',
                          border: '1.5px solid #0e7c7c',
                          borderRadius: '12px',
                          padding: '14px 16px',
                        }}>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#0e7c7c',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            marginBottom: '10px',
                            paddingBottom: '8px',
                            borderBottom: '1.5px solid #99f6e4',
                          }}>
                            {section.header}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {visibleItems.map((item, idx) => (
                              <div key={item.label} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '6px 0',
                                borderBottom: idx < visibleItems.length - 1 ? '1px solid #ccfbf1' : 'none',
                              }}>
                                <span style={{ fontSize: '13px', color: '#475569' }}>{item.icon} {item.label}</span>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', textAlign: 'right', maxWidth: '55%' }}>{item.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}



            </div>
            {/* ── Why other frameworks still matter ── */}
            <div style={{ backgroundColor: '#fffbeb', border: '2px solid #fde68a', borderRadius: '16px', padding: '24px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#92400e', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                💡 Why the other frameworks still matter for you
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {secondaryFws.map(fw => (
                  <div key={fw.key} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '12px', backgroundColor: 'white', borderRadius: '10px', border: `2px solid ${fw.color}20` }}>
                    <span style={{ fontSize: '20px', flexShrink: 0 }}>{fw.icon}</span>
                    <div>
                      <p style={{ fontSize: '13px', fontWeight: '700', color: fw.color, margin: '0 0 4px' }}>{fw.label}</p>
                      <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: '1.6' }}>{fw.whyMatters}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Trend Chart */}
            <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 20px' }}>5-Month Trend</h3>
              <div style={{ display: 'flex', gap: '20px' }}>
                {[
                  { key: 'iso', label: 'ISO', color: '#3b82f6' },
                  { key: 'eu', label: 'EU', color: '#8b5cf6' },
                  { key: 'nist', label: 'NIST', color: '#06b6d4' },
                ].map(fw => (
                  <div key={fw.key} style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: fw.color }}>{fw.label}</span>
                      <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '600' }}>
                        +{mockHistory[mockHistory.length - 1][fw.key] - mockHistory[0][fw.key]}%
                      </span>
                    </div>
                    <MiniBarChart data={mockHistory.map(h => ({ value: h[fw.key] }))} color={fw.color} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                      {mockHistory.map(h => (
                        <span key={h.month} style={{ fontSize: '9px', color: '#94a3b8' }}>{h.month}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>


            {/* EU AI Act Risk Badge */}
            <div style={{ backgroundColor: 'white', border: `2px solid ${getRiskColor(onboarding.euAiRisk)}20`, borderRadius: '16px', padding: '24px', borderLeft: `6px solid ${getRiskColor(onboarding.euAiRisk)}` }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 16px' }}>EU AI Act Classification</h3>
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>{getRiskIcon(onboarding.euAiRisk)}</div>
                <div style={{ display: 'inline-block', backgroundColor: `${getRiskColor(onboarding.euAiRisk)}15`, color: getRiskColor(onboarding.euAiRisk), padding: '8px 20px', borderRadius: '20px', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
                  {onboarding.euAiRisk}
                </div>
                <p style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6', margin: 0 }}>
                  {onboarding.euAiRisk === 'High Risk' ? 'Full compliance required by Dec 2, 2027 (standalone) or Aug 2, 2028 (regulated products). Begin your compliance program now.' :
                   onboarding.euAiRisk === 'Limited Risk' ? 'Transparency obligations apply. Users must be informed they are interacting with AI.' :
                   onboarding.euAiRisk === 'Minimal Risk' ? 'No mandatory obligations. Voluntary best practices recommended.' :
                   'Consult legal counsel immediately. Some uses may be prohibited.'}
                </p>
              </div>
            </div>

          </div>
        )}

        {/* ══════════════════════════════ */}
        {/* FRAMEWORKS TAB */}
        {/* ══════════════════════════════ */}
        {activeTab === 'frameworks' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>
            {[
              { key: 'iso', label: 'ISO/IEC 42001', icon: '📘', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', pct: scores.isoPercent, items: scores.iso, total: scores.isoTotal, ref: 'AI Management System Standard', desc: 'Establishes requirements for an AI management system. Covers governance, risk management, data, and continual improvement.', keyReqs: ['AI Policy (Clause 5.2)', 'Risk Management (Clause 6.1)', 'AI Impact Assessment (Clause 8.2)', 'Performance Monitoring (Clause 9.1)', 'Continual Improvement (Clause 10.1)'] },
              { key: 'eu', label: 'EU AI Act', icon: <EUFlagIcon size={32} blue="#4285F4" glow={false} />, color: '#8b5cf6', bg: '#faf5ff', border: '#ddd6fe', pct: scores.euPercent, items: scores.eu, total: scores.euTotal, ref: 'Regulation (EU) 2024/1689', desc: 'The world\'s first comprehensive AI regulation. Classifies AI systems by risk and sets mandatory requirements for high-risk AI.', keyReqs: ['Risk Management System (Art.9)', 'Data Governance (Art.10)', 'Technical Documentation (Art.11)', 'Human Oversight (Art.14)', 'Post-market Monitoring (Art.72)'] },
              { key: 'nist', label: 'NIST AI RMF', icon: '🏛️', color: '#06b6d4', bg: '#ecfeff', border: '#a5f3fc', pct: scores.nistPercent, items: scores.nist, total: scores.nistTotal, ref: 'AI RMF 1.0 (January 2023)', desc: 'Voluntary US framework for managing AI risks. Four core functions: GOVERN, MAP, MEASURE, MANAGE.', keyReqs: ['GOVERN: Accountability & policies', 'MAP: AI context and categorization', 'MEASURE: Risk analysis & testing', 'MANAGE: Risk treatment & response', 'Trustworthy AI characteristics'] },
            ].sort((a, b) => {
              if (a.key === primaryFramework) return -1;
              if (b.key === primaryFramework) return 1;
              return 0;
            }).map((fw, idx) => (
              <div key={fw.label} style={{ backgroundColor: 'white', border: `${idx === 0 && primaryFramework ? '3px' : '2px'} solid ${idx === 0 && primaryFramework ? fw.color : fw.border}`, borderRadius: '16px', padding: '24px', borderLeft: `6px solid ${fw.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>{fw.icon} {fw.label}</h3>
                      {idx === 0 && primaryFramework && <span style={{ fontSize: '10px', backgroundColor: fw.color, color: 'white', padding: '2px 8px', borderRadius: '20px', fontWeight: '700' }}>YOUR FOCUS</span>}
                    </div>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>{fw.ref}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: fw.color }}><AnimatedNumber value={fw.pct} suffix="%" /></div>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>{fw.items}/{fw.total} items</p>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ height: '10px', backgroundColor: fw.bg, borderRadius: '5px', overflow: 'hidden', marginBottom: '16px' }}>
                  <div style={{ width: `${fw.pct}%`, height: '100%', backgroundColor: fw.color, borderRadius: '5px', transition: 'width 1.2s ease' }} />
                </div>

                <p style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6', marginBottom: '16px' }}>{fw.desc}</p>

                <div>
                  <p style={{ fontSize: '12px', fontWeight: '600', color: '#334155', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Key Requirements</p>
                  {fw.keyReqs.map((req, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: fw.color, flexShrink: 0 }} />
                      <span style={{ fontSize: '13px', color: '#334155' }}>{req}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ══════════════════════════════ */}
        {/* TIMELINE TAB */}
        {/* ══════════════════════════════ */}
        {activeTab === 'timeline' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>

            {/* ── Header row: Timeline title + Countdown title ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: '20px', marginBottom: '0px' }}>

              {/* Timeline header */}
              <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px 16px 0 0', borderBottom: 'none', padding: '20px 24px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
                    {[...new Set(deadlines.map(d => d.framework).filter(Boolean))].join(' · ') || 'EU AI Act'} Enforcement Timeline
                  </h3>
                  {deadlinesLoading && <span style={{ fontSize: '12px', color: '#94a3b8' }}>Loading...</span>}
                </div>
              </div>

              {/* Countdown header */}
              <div style={{ backgroundColor: 'white', border: '2px solid #fecaca', borderLeft: '6px solid #ef4444', borderRadius: '16px 16px 0 0', borderBottom: 'none', padding: '20px 24px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>⏰ Compliance Deadlines Countdown</h3>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', backgroundColor: '#f1f5f9', padding: '4px 10px', borderRadius: '20px', fontFamily: 'monospace', letterSpacing: '0.03em' }}>
                      🌐 {estTime}
                    </div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', textAlign: 'right' }}>
                      Deadlines calculated from this date
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── In Force section label row ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: '20px' }}>
              <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderTop: 'none', borderBottom: 'none', padding: '8px 24px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>✓ In Force</div>
              </div>
              <div style={{ backgroundColor: 'white', border: '2px solid #fecaca', borderLeft: '6px solid #ef4444', borderTop: 'none', borderBottom: 'none', padding: '8px 24px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>✓ In Force</div>
              </div>
            </div>

            {/* ── Shared rows: one per deadline (sorted ascending) ── */}
            {(() => {
              const pastItems = enforcementDates.filter(d => d.status === 'past');
              const upcomingItems = enforcementDates.filter(d => d.status !== 'past');
              const countdownPast = countdownItems.filter(d => d.isPast);
              const countdownUpcoming = countdownItems.filter(d => !d.isPast);
              const maxPast = Math.max(pastItems.length, countdownPast.length);
              const maxUpcoming = Math.max(upcomingItems.length, countdownUpcoming.length);

              const rows = [];

              // Past rows
              for (let i = 0; i < maxPast; i++) {
                const tl = pastItems[i];
                const cd = countdownPast[i];
                rows.push(
                  <div key={`past-row-${i}`} style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: '20px' }}>
                    {/* Timeline cell */}
                    <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderTop: 'none', borderBottom: 'none', padding: '0 24px 12px' }}>
                      {tl ? (
                        <div style={{ position: 'relative', paddingLeft: '32px' }}>
                          <div style={{ position: 'absolute', left: '6px', width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#94a3b8', border: '3px solid #cbd5e1', top: '6px' }} />
                          <div style={{ backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '12px 14px' }}>
                            {tl.framework && <p style={{ fontSize: '11px', fontWeight: '700', color: '#00D9FF', margin: '0 0 3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tl.framework}</p>}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                              <div>
                                <p style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', margin: '0 0 2px' }}>{tl.label}</p>
                                <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>{tl.ref}</p>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#94a3b8' }}>{tl.date}</span><br />
                                <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '10px', backgroundColor: '#f1f5f9', color: '#64748b', fontWeight: '600' }}>In Force</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : <div />}
                    </div>
                    {/* Countdown cell */}
                    <div style={{ backgroundColor: 'white', border: '2px solid #fecaca', borderLeft: '6px solid #ef4444', borderTop: 'none', borderBottom: 'none', padding: '0 18px 12px' }}>
                      {cd ? (
                        <div style={{ backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', opacity: 0.75 }}>
                          <div style={{ flex: 1 }}>
                            {cd.framework && <p style={{ fontSize: '10px', fontWeight: '700', color: '#00D9FF', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{cd.framework}</p>}
                            <p style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', margin: '0 0 1px' }}>{cd.label}</p>
                            <p style={{ fontSize: '10px', color: '#94a3b8', margin: 0 }}>{cd.ref}</p>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <span style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8', backgroundColor: '#f1f5f9', padding: '2px 8px', borderRadius: '20px' }}>In Force</span>
                            <p style={{ fontSize: '10px', color: '#94a3b8', margin: '3px 0 0' }}>{cd.date}</p>
                          </div>
                        </div>
                      ) : <div />}
                    </div>
                  </div>
                );
              }

              // Divider row
              rows.push(
                <div key="divider-row" style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: '20px' }}>
                  <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderTop: 'none', borderBottom: 'none', padding: '4px 24px' }}>
                    <div style={{ borderTop: '2px dashed #e2e8f0' }} />
                  </div>
                  <div style={{ backgroundColor: 'white', border: '2px solid #fecaca', borderLeft: '6px solid #ef4444', borderTop: 'none', borderBottom: 'none', padding: '4px 18px' }}>
                    <div style={{ borderTop: '2px dashed #fecaca' }} />
                  </div>
                </div>
              );

              // Upcoming section label row
              rows.push(
                <div key="upcoming-label" style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: '20px' }}>
                  <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderTop: 'none', borderBottom: 'none', padding: '8px 24px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.08em' }}>⏳ Upcoming</div>
                  </div>
                  <div style={{ backgroundColor: 'white', border: '2px solid #fecaca', borderLeft: '6px solid #ef4444', borderTop: 'none', borderBottom: 'none', padding: '8px 18px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.08em' }}>⏳ Upcoming</div>
                  </div>
                </div>
              );

              // Upcoming rows
              for (let i = 0; i < maxUpcoming; i++) {
                const tl = upcomingItems[i];
                const cd = countdownUpcoming[i];
                const isLast = i === maxUpcoming - 1;
                rows.push(
                  <div key={`upcoming-row-${i}`} style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: '20px' }}>
                    {/* Timeline cell */}
                    <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderTop: 'none', borderBottom: isLast ? '2px solid #e2e8f0' : 'none', borderRadius: isLast ? '0 0 16px 16px' : '0', padding: `0 24px ${isLast ? '20px' : '12px'}` }}>
                      {tl ? (
                        <div style={{ position: 'relative', paddingLeft: '32px' }}>
                          <div style={{ position: 'absolute', left: '6px', width: '14px', height: '14px', borderRadius: '50%', backgroundColor: tl.urgent ? '#ef4444' : '#3b82f6', border: `3px solid ${tl.urgent ? '#fecaca' : '#bfdbfe'}`, top: '6px' }} />
                          <div style={{ backgroundColor: tl.urgent ? '#fef2f2' : '#eff6ff', border: `2px solid ${tl.urgent ? '#fecaca' : '#bfdbfe'}`, borderRadius: '12px', padding: '12px 14px' }}>
                            {tl.framework && <p style={{ fontSize: '11px', fontWeight: '700', color: '#00D9FF', margin: '0 0 3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tl.framework}</p>}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                              <div>
                                <p style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a', margin: '0 0 2px' }}>{tl.label}</p>
                                <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>{tl.ref}</p>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <span style={{ fontSize: '13px', fontWeight: 'bold', color: tl.urgent ? '#ef4444' : '#3b82f6' }}>{tl.date}</span><br />
                                <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '10px', backgroundColor: '#dcfce7', color: '#15803d', fontWeight: '600' }}>Upcoming</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : <div />}
                    </div>
                    {/* Countdown cell */}
                    <div style={{ backgroundColor: 'white', border: '2px solid #fecaca', borderLeft: '6px solid #ef4444', borderTop: 'none', borderBottom: isLast ? '2px solid #fecaca' : 'none', borderRadius: isLast ? '0 0 16px 16px' : '0', padding: `0 18px ${isLast ? '20px' : '12px'}` }}>
                      {cd ? (
                        <div style={{ backgroundColor: cd.urgent ? '#fef2f2' : '#eff6ff', border: `2px solid ${cd.urgent ? '#fecaca' : '#bfdbfe'}`, borderRadius: '12px', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                          <div style={{ flex: 1 }}>
                            {cd.framework && <p style={{ fontSize: '10px', fontWeight: '700', color: '#00D9FF', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{cd.framework}</p>}
                            <p style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', margin: '0 0 1px' }}>{cd.label}</p>
                            <p style={{ fontSize: '10px', color: '#94a3b8', margin: '0 0 3px' }}>{cd.ref}</p>
                            {cd.fine_specific && <p style={{ fontSize: '10px', color: '#64748b', margin: 0 }}>Fine: <strong>{cd.fine_specific}</strong></p>}
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: cd.urgent ? '#ef4444' : '#3b82f6', lineHeight: 1 }}>{cd.daysLeft}</div>
                            <p style={{ fontSize: '10px', color: '#64748b', margin: '2px 0 0' }}>days · {cd.date}</p>
                          </div>
                        </div>
                      ) : <div />}
                    </div>
                  </div>
                );
              }

              return rows;
            })()}
          </div>
        )}

        {/* ══════════════════════════════ */}
        {/* ACTIONS TAB */}
        {/* ══════════════════════════════ */}
        {activeTab === 'actions' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>

            {/* Progress summary */}
            <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px', padding: '24px', gridColumn: 'span 2' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 4px' }}>Compliance Action Plan</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>{completedActions} of {complianceActions.length} actions completed</p>
                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', fontWeight: '600',
                      backgroundColor: riskKey === 'unacceptable' ? '#fef2f2' : riskKey === 'high' ? '#fffbeb' : riskKey === 'limited' ? '#eff6ff' : '#f0fdf4',
                      color: riskKey === 'unacceptable' ? '#991b1b' : riskKey === 'high' ? '#92400e' : riskKey === 'limited' ? '#1e40af' : '#065f46'
                    }}>
                      {onboarding.euAiRisk} · {complianceActions.length} actions
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: getColor(actionProgress) }}>
                  <AnimatedNumber value={actionProgress} suffix="%" />
                </div>
              </div>
              <div style={{ height: '10px', backgroundColor: '#f1f5f9', borderRadius: '5px', overflow: 'hidden', marginBottom: '20px' }}>
                <div style={{ width: `${actionProgress}%`, height: '100%', backgroundColor: getColor(actionProgress), borderRadius: '5px', transition: 'width 0.8s ease' }} />
              </div>

               {/* Action items — grouped by priority */}
               <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                 {complianceActions.map((action, i) => {
                   const prevPriority = i > 0 ? complianceActions[i - 1].priority : null;
                   const priorityLabel = { critical: '🔴 Critical', high: '🟡 High', medium: '🔵 Medium', low: '⚪ Low' }[action.priority] || action.priority;
                   const isFirstOfGroup = i === 0 || prevPriority !== action.priority;
                   const bg = actionStates[i] ? '#f0fdf4' : action.priority === 'critical' ? '#fef2f2' : action.priority === 'high' ? '#fffbeb' : '#f8fafc';
                   const bc = actionStates[i] ? '#bbf7d0' : action.priority === 'critical' ? '#fecaca' : action.priority === 'high' ? '#fde68a' : '#e2e8f0';
                   return (
                     <React.Fragment key={i}>
                       {isFirstOfGroup && (
                         <div style={{
                           display: 'flex', alignItems: 'center', gap: '8px',
                           margin: i === 0 ? '0 0 6px' : '16px 0 6px',
                           paddingBottom: '6px',
                           borderBottom: '2px solid #f1f5f9'
                         }}>
                           <span style={{
                             fontSize: '11px', fontWeight: '700',
                             color: action.priority === 'critical' ? '#991b1b' : action.priority === 'high' ? '#92400e' : action.priority === 'medium' ? '#1e40af' : '#374151',
                             textTransform: 'uppercase', letterSpacing: '0.08em'
                           }}>
                             {priorityLabel}
                           </span>
                           <span style={{
                             fontSize: '10px', color: '#94a3b8',
                             backgroundColor: '#f1f5f9',
                             padding: '1px 8px', borderRadius: '10px'
                           }}>
                             {complianceActions.filter(a => a.priority === action.priority).length} actions
                           </span>
                         </div>
                       )}
                       <div
                         onClick={() => {
                           const next = [...actionStates];
                           next[i] = !next[i];
                           setActionStates(next);
                           localStorage.setItem(userKey(`qais_action_states_${riskKey}`), JSON.stringify(next));
                         }}
                         style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', backgroundColor: bg, border: `2px solid ${bc}`, transition: 'all 0.2s' }}
                       >
                         <div style={{ width: '22px', height: '22px', borderRadius: '6px', border: `2px solid ${actionStates[i] ? '#10b981' : '#cbd5e1'}`, backgroundColor: actionStates[i] ? '#10b981' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                           {actionStates[i] && <span style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>✓</span>}
                         </div>
                         <div style={{ flex: 1 }}>
                           <p style={{ fontSize: '14px', fontWeight: '600', color: actionStates[i] ? '#64748b' : '#0f172a', margin: '0 0 4px', textDecoration: actionStates[i] ? 'line-through' : 'none' }}>{action.text}</p>
                           <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                             <span style={{ fontSize: '12px', color: '#64748b' }}>📋 {action.framework}</span>
                             <span style={{ fontSize: '12px', color: '#64748b' }}>⏰ {action.deadline}</span>
                           </div>
                         </div>
                       </div>
                     </React.Fragment>
                   );
                 })}
               </div>
            </div>
          </div>
        )}
		
		
	    {/* ══════════════════════════════ */}
        {/* ai-decisions TAB */}
        {/* ══════════════════════════════ */}	
		  {activeTab === 'ai-decisions' && (
			<AIDecisionsTab light={true} currentUserEmail={userEmail} />
			)}
		
		
        {/* ── Footer ── */}
        <div style={{ marginTop: '32px', padding: '16px 20px', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }} />
              Sealed on Quantum-Safe Blockchain 
            </span>
            <span style={{ fontSize: '14px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#8b5cf6', display: 'inline-block' }} />
              ISO 42001 · EU AI Act · NIST RMF
            </span>
          </div>
          <p style={{ fontSize: '14px', color: '#94a3b8', margin: 0 }}>
            www.QuantumAIShield.com · Data updated {lastUpdated}
          </p>
        </div>

      </div>
    </div>
  );
}

export default Dashboard;